import { Component, Inject } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';

export interface EndSubscriptionDialogData {
  title?: string;
  message?: string;
  initialDate?: Date;
  // The subscription payload (already shaped for the updatesubscription endpoint) minus endDate —
  // the dialog fills in endDate itself and posts it, so it can show a validation error and let the
  // user pick a different date without leaving the dialog.
  basePayload: any;
  user: string;
}

@Component({
  selector: 'app-end-subscription-dialog',
  templateUrl: './end-subscription-dialog.component.html',
  styleUrls: ['./end-subscription-dialog.component.css'],
  standalone: false
})
export class EndSubscriptionDialogComponent {
  endDate: Date;
  saving = false;
  errorMessage: string | null = null;

  constructor(
    private http: HttpClient,
    public dialogRef: MatDialogRef<EndSubscriptionDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: EndSubscriptionDialogData
  ) {
    this.endDate = data?.initialDate ?? new Date();
  }

  confirm() {
    if (!this.endDate || this.saving) return;

    this.errorMessage = null;
    this.saving = true;

    // The datepicker hands back a Moment (not a plain Date); JSON.stringify would call its toJSON()
    // and convert to a UTC instant, which can roll the calendar date back a day for any timezone
    // ahead of UTC (this is exactly what produced the "picked the 10th, error said the 9th" bug).
    // Send a plain yyyy-MM-dd string instead so the date can't shift in transit.
    const payload = { ...this.data.basePayload, endDate: this.toIsoDate(this.endDate) };
    const params = new HttpParams()
      .set('Subscription', JSON.stringify(payload))
      .set('user', this.data.user);

    this.http.post<boolean>('/charitycentral/updatesubscription', null, { params })
      .subscribe({
        next: () => {
          this.saving = false;
          this.dialogRef.close(this.endDate);
        },
        error: err => {
          this.saving = false;
          this.errorMessage = (err?.error && typeof err.error === 'string')
            ? err.error
            : 'Unable to end this subscription with the selected date.';
        }
      });
  }

  cancel() {
    this.dialogRef.close(null);
  }

  private toIsoDate(d: any): string {
    if (typeof d?.format === 'function') return d.format('YYYY-MM-DD');
    const date = new Date(d);
    const year = date.getFullYear();
    const month = (date.getMonth() +1).toString().padStart(2, '0');
    const day = date.getDate().toString().padStart(2, '0');
    return `${year}-${month}-${day}`;
  }
}
